import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  ContentCopy,
  DeleteOutline,
  Edit,
  FormatListBulleted,
  Link as LinkIcon,
  Lock,
  People,
  PersonAdd,
  Public,
  Search,
} from '@mui/icons-material';
import { useNotification } from './NotificationContext';
import SignInNudge from './SignInNudge';
import { authService } from '../services/authService';
import { diveSiteListService } from '../services/diveSiteListService';
import { buddyService, UserPublicProfile } from '../services/buddyService';
import type { DiveSiteList, DiveSiteListVisibility, CreateDiveSiteListRequest } from '../types/DiveSite';

const VISIBILITY_OPTIONS: { value: DiveSiteListVisibility; label: string; icon: React.ReactElement }[] = [
  { value: 'private', label: 'Private', icon: <Lock fontSize="small" /> },
  { value: 'invite-only', label: 'Invite only', icon: <People fontSize="small" /> },
  { value: 'link', label: 'Shared by link', icon: <LinkIcon fontSize="small" /> },
];

const visibilityLabel = (v: DiveSiteListVisibility) =>
  VISIBILITY_OPTIONS.find((o) => o.value === v)?.label ?? v;

const visibilityIcon = (v: DiveSiteListVisibility) =>
  VISIBILITY_OPTIONS.find((o) => o.value === v)?.icon ?? <Public fontSize="small" />;

interface ListFormState {
  name: string;
  description: string;
  visibility: DiveSiteListVisibility;
}

const emptyForm = (): ListFormState => ({ name: '', description: '', visibility: 'private' });

const DiveLists: React.FC = () => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();

  const [lists, setLists] = useState<DiveSiteList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<ListFormState>(emptyForm());
  const [createSaving, setCreateSaving] = useState(false);

  const [editTarget, setEditTarget] = useState<DiveSiteList | null>(null);
  const [editForm, setEditForm] = useState<ListFormState>(emptyForm());
  const [editSaving, setEditSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<DiveSiteList | null>(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  const [shareTarget, setShareTarget] = useState<DiveSiteList | null>(null);
  const [buddyOptions, setBuddyOptions] = useState<UserPublicProfile[]>([]);
  const [selectedBuddies, setSelectedBuddies] = useState<UserPublicProfile[]>([]);
  const [shareSaving, setShareSaving] = useState(false);

  const currentUser = authService.getUser();
  const isAuthenticated = authService.isAuthenticated();
  const [, forceAuthRefresh] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await diveSiteListService.getMyDiveSiteLists();
        setLists(data);
      } catch (err) {
        console.error('Failed to load dive lists:', err);
        setError(err instanceof Error ? err.message : 'Failed to load dive lists.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isAuthenticated]);

  const filteredLists = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return lists;
    return lists.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        (l.description ?? '').toLowerCase().includes(q)
    );
  }, [lists, searchQuery]);

  const handleCreate = async () => {
    if (!createForm.name.trim()) return;
    setCreateSaving(true);
    try {
      const req: CreateDiveSiteListRequest = {
        name: createForm.name.trim(),
        description: createForm.description.trim() || undefined,
        visibility: createForm.visibility,
        siteIds: [],
        collaboratorUserIds: [],
        siteSchedules: {},
      };
      const created = await diveSiteListService.createDiveSiteList(req);
      setLists((prev) => [created, ...prev]);
      setCreateOpen(false);
      setCreateForm(emptyForm());
      showSuccess(`"${created.name}" created.`);
      navigate(`/dive-site-lists/${created.listId}`);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to create list.');
    } finally {
      setCreateSaving(false);
    }
  };

  const handleEditOpen = (list: DiveSiteList) => {
    setEditTarget(list);
    setEditForm({ name: list.name, description: list.description ?? '', visibility: list.visibility as DiveSiteListVisibility });
  };

  const handleEditSave = async () => {
    if (!editTarget || !editForm.name.trim()) return;
    setEditSaving(true);
    try {
      const updated = await diveSiteListService.updateDiveSiteList({
        listId: editTarget.listId,
        name: editForm.name.trim(),
        description: editForm.description.trim() || undefined,
        visibility: editForm.visibility,
        siteIds: editTarget.siteIds,
        collaboratorUserIds: editTarget.collaboratorUserIds,
        siteSchedules: editTarget.siteSchedules,
      });
      setLists((prev) => prev.map((l) => (l.listId === updated.listId ? updated : l)));
      setEditTarget(null);
      showSuccess(`"${updated.name}" updated.`);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update list.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteInProgress(true);
    try {
      await diveSiteListService.deleteDiveSiteList(deleteTarget.listId);
      setLists((prev) => prev.filter((l) => l.listId !== deleteTarget.listId));
      showSuccess(`"${deleteTarget.name}" deleted.`);
      setDeleteTarget(null);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete list.');
    } finally {
      setDeleteInProgress(false);
    }
  };

  const handleShareOpen = async (list: DiveSiteList) => {
    setShareTarget(list);
    setSelectedBuddies([]);
    if (!currentUser?.userId) return;
    try {
      const buddies = await buddyService.getBuddies(currentUser.userId);
      const profiles = await Promise.allSettled(buddies.map(b => buddyService.getUserProfile(b.buddyUserId)));
      const options: UserPublicProfile[] = [];
      profiles.forEach((r) => { if (r.status === 'fulfilled') options.push(r.value); });
      const existingIds = new Set(list.collaboratorUserIds);
      setBuddyOptions(options.filter(p => !existingIds.has(p.userId)));
    } catch {
      setBuddyOptions([]);
    }
  };

  const handleShareSave = async () => {
    if (!shareTarget || selectedBuddies.length === 0) return;
    setShareSaving(true);
    try {
      const newCollaborators = Array.from(new Set([...shareTarget.collaboratorUserIds, ...selectedBuddies.map(b => b.userId)]));
      const updated = await diveSiteListService.updateDiveSiteList({
        ...shareTarget,
        collaboratorUserIds: newCollaborators,
      });
      setLists(prev => prev.map(l => l.listId === updated.listId ? updated : l));
      showSuccess(`Shared with ${selectedBuddies.length} buddy${selectedBuddies.length !== 1 ? 'ies' : ''}.`);
      setShareTarget(null);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to share list.');
    } finally {
      setShareSaving(false);
    }
  };

  const handleCopyShareLink = async (list: DiveSiteList) => {
    if (!list.shareCode) return;
    const url = `${window.location.origin}/dive-site-lists/shared/${list.shareCode}`;
    try {
      await navigator.clipboard.writeText(url);
      showSuccess('Share link copied to clipboard.');
    } catch {
      showError(`Share link: ${url}`);
    }
  };

  if (!isAuthenticated) {
    return (
      <Box>
        <SignInNudge
          feature="create and manage dive lists"
          description="Build wish lists of dive sites, track the ones you've visited, and share lists with your buddies — sign in or create a free account to get started."
          onSignedIn={() => forceAuthRefresh(v => v + 1)}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 2.5 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormatListBulleted color="primary" />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              My Dive Lists
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Saveable, shareable bucket lists of dive sites
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
          New list
        </Button>
      </Stack>

      <TextField
        fullWidth
        size="small"
        placeholder="Search lists by name or description…"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2.5 }}
      />

      {loading ? (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={20} />
          <Typography variant="body1">Loading your lists…</Typography>
        </Stack>
      ) : error ? (
        <Alert severity="warning">{error}</Alert>
      ) : filteredLists.length === 0 ? (
        <Alert severity="info">
          {searchQuery ? 'No lists match your search.' : "You don't have any dive lists yet. Create one to start planning!"}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {filteredLists.map((list) => {
            const isOwner = list.ownerUserId === currentUser?.userId;
            const scheduledCount = Object.keys(list.siteSchedules ?? {}).length;
            const divedSiteCount = Object.keys(list.divedByUserIds ?? {}).filter(
              (sid) => (list.divedByUserIds[sid] ?? []).includes(currentUser?.userId ?? '')
            ).length;

            return (
              <Grid item xs={12} md={6} lg={4} key={list.listId}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                      <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
                        {list.name}
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        {isOwner && (
                          <Tooltip title="Edit list">
                            <IconButton size="small" onClick={() => handleEditOpen(list)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isOwner && (
                          <Tooltip title="Delete list">
                            <IconButton size="small" color="error" onClick={() => setDeleteTarget(list)}>
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </Stack>

                    {list.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {list.description}
                      </Typography>
                    )}

                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
                      <Chip size="small" icon={visibilityIcon(list.visibility as DiveSiteListVisibility)} label={visibilityLabel(list.visibility as DiveSiteListVisibility)} />
                      <Chip size="small" label={`${list.siteIds.length} site${list.siteIds.length !== 1 ? 's' : ''}`} />
                      {list.collaboratorUserIds.length > 0 && (
                        <Chip size="small" icon={<People fontSize="small" />} label={`${list.collaboratorUserIds.length} diver${list.collaboratorUserIds.length !== 1 ? 's' : ''}`} />
                      )}
                      {scheduledCount > 0 && (
                        <Chip size="small" color="secondary" label={`${scheduledCount} scheduled`} />
                      )}
                      {divedSiteCount > 0 && (
                        <Chip size="small" color="success" label={`${divedSiteCount} dived`} />
                      )}
                    </Stack>

                    {!isOwner && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Shared by {list.ownerDisplayName || 'another diver'}
                      </Typography>
                    )}
                  </CardContent>
                  <Divider />
                  <CardActions sx={{ px: 2, py: 1 }}>
                    <Button size="small" onClick={() => navigate(`/dive-site-lists/${list.listId}`)}>
                      View & edit sites
                    </Button>
                    {isOwner && (
                      <Tooltip title="Share with dive buddies">
                        <IconButton size="small" color="primary" onClick={() => handleShareOpen(list)}>
                          <PersonAdd fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {list.shareCode && (
                      <Tooltip title="Copy share link">
                        <IconButton size="small" onClick={() => handleCopyShareLink(list)}>
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New dive list</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="List name"
              fullWidth
              required
              value={createForm.name}
              onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
              inputProps={{ maxLength: 120 }}
            />
            <TextField
              label="Description (optional)"
              fullWidth
              multiline
              rows={2}
              value={createForm.description}
              onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
            />
            <TextField
              label="Visibility"
              select
              fullWidth
              value={createForm.visibility}
              onChange={(e) => setCreateForm((f) => ({ ...f, visibility: e.target.value as DiveSiteListVisibility }))}
            >
              {VISIBILITY_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {o.icon}
                    <span>{o.label}</span>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={createSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={createSaving || !createForm.name.trim()}>
            {createSaving ? <CircularProgress size={18} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={Boolean(editTarget)} onClose={() => setEditTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>Edit list</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="List name"
              fullWidth
              required
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              inputProps={{ maxLength: 120 }}
            />
            <TextField
              label="Description (optional)"
              fullWidth
              multiline
              rows={2}
              value={editForm.description}
              onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
            />
            <TextField
              label="Visibility"
              select
              fullWidth
              value={editForm.visibility}
              onChange={(e) => setEditForm((f) => ({ ...f, visibility: e.target.value as DiveSiteListVisibility }))}
            >
              {VISIBILITY_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {o.icon}
                    <span>{o.label}</span>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)} disabled={editSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editSaving || !editForm.name.trim()}>
            {editSaving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete list?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteInProgress}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleteInProgress}>
            {deleteInProgress ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share with buddies dialog */}
      <Dialog
        open={Boolean(shareTarget)}
        onClose={() => setShareTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <People color="primary" />
            <span>Share with Dive Buddies</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add buddies as collaborators on <strong>{shareTarget?.name}</strong>.
          </Typography>
          {buddyOptions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No buddies to add — all existing buddies are already collaborators, or you have no buddies yet.
            </Typography>
          ) : (
            <Autocomplete
              multiple
              options={buddyOptions}
              getOptionLabel={(o) => o.displayName}
              value={selectedBuddies}
              onChange={(_, v) => setSelectedBuddies(v)}
              renderOption={(props, option) => (
                <li {...props} key={option.userId}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.8rem' }}>
                      {option.displayName.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{option.displayName}</Typography>
                      <Typography variant="caption" color="text.secondary">{option.diveCount} dives</Typography>
                    </Box>
                  </Stack>
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.userId}
                    label={option.displayName}
                    size="small"
                    avatar={<Avatar sx={{ bgcolor: 'primary.main' }}>{option.displayName.charAt(0).toUpperCase()}</Avatar>}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Select buddies" placeholder="Search…" />
              )}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareTarget(null)} disabled={shareSaving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleShareSave}
            disabled={shareSaving || selectedBuddies.length === 0}
            startIcon={shareSaving ? <CircularProgress size={16} /> : <PersonAdd />}
          >
            Share
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DiveLists;
